import { SxProps, Theme } from "@mui/material";
import tokens from "./AiWebForm.tokens";

const { color, layout, radius, font } = tokens;

/** Clamps a design value (px) to a 44px touch target without changing how it looks. */
const touch = { position: "relative", "&::after": { content: '""', position: "absolute", inset: -4 } };

const focusRing = {
  "&:focus-visible": { outline: `2px solid ${color.brand}`, outlineOffset: "2px" },
};

const plainButton = {
  appearance: "none",
  border: 0,
  background: "none",
  padding: 0,
  margin: 0,
  font: "inherit",
  color: "inherit",
  cursor: "pointer",
  ...focusRing,
};

const pill = {
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
  borderRadius: radius.pill,
  whiteSpace: "nowrap",
};

const outlinedSquare = {
  ...plainButton,
  boxSizing: "border-box",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  background: color.surface,
  border: `1px solid ${color.border}`,
  borderRadius: radius.iconButton,
  color: color.body,
  "&:hover": { background: color.subtle },
};

const notice = {
  boxSizing: "border-box",
  display: "flex",
  alignItems: "flex-start",
  gap: "9px",
  padding: "9px 11px",
  borderRadius: radius.notice,
  border: "1px solid",
  ...font.small,
  lineHeight: "18px",
  "& svg": { width: 16, height: 16, flexShrink: 0, mt: "1px" },
};

/**
 * A phone on its side (or any window under 560 px tall) cannot hold the fixed
 * chrome and a conversation at once: there the page scrolls as one, with the
 * composer stuck to the bottom.
 */
const SHORT = "@media (max-height: 560px)";

const raw = {
  // ------------------------------------------------------------ page shell
  page: {
    height: "100dvh",
    display: "flex",
    flexDirection: "column",
    background: color.surface,
    color: color.ink,
    overflow: "hidden",
    [SHORT]: { height: "auto", minHeight: "100dvh", overflow: "visible" },
  },
  body: { flex: 1, minHeight: 0, display: "flex", position: "relative", [SHORT]: { flex: "none" } },

  // ------------------------------------------------------------ header
  header: {
    boxSizing: "border-box",
    height: layout.headerHeight,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: { xs: "10px", md: "16px" },
    px: { xs: `${layout.phoneHeaderPadX}px`, md: `${layout.headerPadX}px` },
    background: color.surface,
    borderBottom: `1px solid ${color.divider}`,
  },
  backButton: { ...outlinedSquare, ...touch, width: 38, height: 38, "& svg": { width: 18, height: 18 } },
  titleBlock: { minWidth: 0, flex: { xs: 1, md: "0 1 auto" }, display: "flex", flexDirection: "column", gap: { xs: "4px", md: "3px" } },
  titleRow: { display: "flex", alignItems: "center", gap: "10px", minWidth: 0 },
  title: {
    ...font.title,
    m: 0,
    color: color.ink,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
  },
  aiBadge: {
    ...pill,
    ...font.badge,
    padding: "4px 8px",
    background: color.brandTint,
    color: color.brand,
    flexShrink: 0,
    "& svg": { width: 11, height: 11 },
  },
  subRow: { display: "flex", alignItems: "center", gap: "6px", minWidth: 0 },
  templateLine: {
    ...font.small,
    color: color.sub,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
  },
  headerSpacer: { flex: 1, minWidth: 0, display: { xs: "none", md: "block" } },
  publishStatus: { display: { xs: "none", lg: "inline-flex" }, alignItems: "center", gap: "6px", ...font.small, color: color.sub, whiteSpace: "nowrap" },
  statusDot: (published: boolean) => ({
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: published ? color.successText : color.strongBorder,
  }),
  versionButton: {
    ...plainButton,
    boxSizing: "border-box",
    height: 40,
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
    pl: "14px",
    pr: "12px",
    border: `1px solid ${color.border}`,
    borderRadius: radius.control,
    background: color.surface,
    color: color.ink,
    ...font.control,
    whiteSpace: "nowrap",
    "& svg": { width: 16, height: 16, color: color.sub },
    "&:hover": { background: color.subtle },
    "&[disabled]": { cursor: "default", color: color.sub },
  },
  currentBadge: { ...pill, ...font.badge, padding: "4px 8px", background: color.successBg, color: color.successText },
  publishedBadge: { ...pill, ...font.badge, padding: "4px 8px", background: color.infoBg, color: color.infoText },
  publishButton: {
    height: 40,
    px: { xs: "16px", md: "18px" },
    flexShrink: 0,
    "& svg": { width: 16, height: 16 },
  },

  // ------------------------------------------------------------ phone tab bar
  tabBar: {
    boxSizing: "border-box",
    height: layout.tabBarHeight,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    px: "12px",
    background: color.surface,
    borderBottom: `1px solid ${color.border}`,
  },
  segmented: { display: "inline-flex", gap: "2px", p: "3px", borderRadius: radius.iconButton, background: color.chipBg, flexShrink: 0 },
  segment: (selected: boolean) => ({
    ...plainButton,
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    minHeight: 29,
    px: "14px",
    py: "7px",
    boxSizing: "border-box",
    borderRadius: radius.smallTile,
    ...font.smallBold,
    color: selected ? color.ink : color.sub,
    background: selected ? color.surface : "transparent",
    boxShadow: selected ? `0 1px 1.5px ${color.segmentShadow}` : "none",
    "& svg": { width: 15, height: 15 },
  }),
  /** 900–1199 px: the preview pane is narrow, so the size switch shows its icons only. */
  segmentLabel: { display: { xs: "inline", md: "none", lg: "inline" } },
  updatedDot: { position: "absolute", top: 5, right: 6, width: 7, height: 7, borderRadius: "50%", background: color.brand },
  /** Read by screen readers only. */
  srOnly: {
    position: "absolute",
    width: "1px",
    height: "1px",
    p: 0,
    m: "-1px",
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    whiteSpace: "nowrap",
    border: 0,
  },
  tabSpacer: { flex: 1 },

  // ------------------------------------------------------------ chat panel
  chat: (width: number | string) => ({
    boxSizing: "border-box",
    width,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    background: color.surface,
    borderRight: { xs: 0, md: `1px solid ${color.divider}` },
    [SHORT]: { minHeight: "auto" },
  }),
  chatHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    pt: "20px",
    pb: "16px",
    px: { xs: `${layout.phoneChatPadX}px`, md: `${layout.chatPadX}px` },
    borderBottom: `1px solid ${color.hairline}`,
    flexShrink: 0,
  },
  designerTile: {
    width: 40,
    height: 40,
    borderRadius: radius.tile,
    background: color.brandTint,
    color: color.brand,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    "& svg": { width: 20, height: 20 },
  },
  chatHeaderText: { flex: 1, minWidth: 0 },
  panelTitle: { ...font.panelTitle, color: color.ink, m: 0 },
  panelSubtitle: { ...font.small, color: color.sub, pt: "2px" },
  moreButton: { ...outlinedSquare, ...touch, width: 34, height: 34, "& svg": { width: 16, height: 16 } },
  chatScroll: { flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", [SHORT]: { flex: "none", overflowY: "visible" } },
  chatNotices: { flexShrink: 0, px: { xs: `${layout.phoneChatPadX}px`, md: `${layout.chatPadX}px` }, pt: "14px", display: "flex", flexDirection: "column", gap: "12px" },
  boundaries: { ...notice, border: 0, background: color.subtle, color: color.body, padding: "10px 12px", "& svg": { ...notice["& svg"], color: color.sub } },
  staleBanner: { ...notice, padding: "11px 12px", gap: "10px", background: color.warnBg, borderColor: color.warnBorder, color: color.warnText, fontWeight: 600, "& svg": { ...notice["& svg"], color: color.warnText } },
  bannerText: { flex: 1, minWidth: 0 },
  bannerClose: { ...plainButton, ...touch, color: color.warnText, display: "inline-flex", "& svg": { width: 14, height: 14 } },
  fieldsBanner: { ...notice, flexWrap: "wrap", background: color.warnBg, borderColor: color.warnBorder, color: color.warnText, "& svg": { ...notice["& svg"], color: color.warnText } },
  log: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    gap: "18px",
    pt: "18px",
    pb: "12px",
    px: { xs: `${layout.phoneChatPadX}px`, md: `${layout.chatPadX}px` },
  },
  earlier: { display: "flex", justifyContent: "center" },
  earlierButton: {
    ...plainButton,
    ...pill,
    gap: "6px",
    padding: "6px 12px",
    minHeight: 32,
    background: color.subtle,
    ...font.smallStrong,
    color: color.sub,
    "& svg": { width: 12, height: 12 },
  },
  dayDivider: { display: "flex", alignItems: "center", gap: "10px", ...font.meta, color: color.faint, "&::before, &::after": { content: '""', flex: 1, height: "1px", background: color.lightBorder } },
  systemLine: { textAlign: "center", ...font.meta, color: color.faint },

  // user message
  userBlock: { alignSelf: "flex-end", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "6px", maxWidth: `min(${layout.userBubbleMax}px, 88%)` },
  byline: { display: "flex", alignItems: "center", gap: "6px", ...font.meta, color: color.faint, "& svg": { width: 12, height: 12 } },
  userBubble: (mine: boolean) => ({
    boxSizing: "border-box",
    padding: "10px 14px",
    borderRadius: `${radius.bubble} ${radius.bubble} ${radius.bubbleTail} ${radius.bubble}`,
    background: mine ? color.ink : color.colleagueBubble,
    color: mine ? color.surface : color.ink,
    ...font.body,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
  }),
  styleLine: { display: "block", opacity: 0.75 },
  attachment: {
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "8px 12px 8px 8px",
    border: `1px solid ${color.border}`,
    borderRadius: radius.notice,
    background: color.surface,
    maxWidth: "100%",
  },
  attachmentTile: {
    width: 34,
    height: 34,
    borderRadius: "7px",
    background: color.brandTint,
    color: color.brand,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    overflow: "hidden",
    "& svg": { width: 18, height: 18 },
    "& img": { width: "100%", height: "100%", objectFit: "contain" },
  },
  attachmentText: { minWidth: 0 },
  attachmentName: { ...font.smallStrong, color: color.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  attachmentMeta: { ...font.meta, fontWeight: 500, color: color.sub, pt: "2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },

  // assistant reply
  replyRow: { display: "flex", alignItems: "flex-start", gap: "10px", maxWidth: `${layout.replyMax}px`, width: "100%" },
  replyTile: {
    width: 28,
    height: 28,
    borderRadius: radius.smallTile,
    background: color.brandTint,
    color: color.brand,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    "& svg": { width: 15, height: 15 },
  },
  replyColumn: { minWidth: 0, flex: 1, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "8px" },
  replyBubble: {
    boxSizing: "border-box",
    padding: "11px 14px",
    borderRadius: `${radius.bubbleTail} ${radius.bubble} ${radius.bubble} ${radius.bubble}`,
    border: `1px solid ${color.border}`,
    background: color.surface,
    color: color.ink,
    ...font.body,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    maxWidth: "100%",
  },
  changed: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px" },
  changedLabel: { ...font.eyebrow, letterSpacing: "0.55px", color: color.sub, mr: "2px" },
  changeChip: { ...pill, ...font.smallStrong, padding: "4px 9px", background: color.successBg, color: color.successText, "& svg": { width: 12, height: 12 } },
  warning: { ...notice, background: color.warnBg, borderColor: color.warnBorder, color: color.warnText, "& svg": { ...notice["& svg"], color: color.warnText } },
  infoNotice: { ...notice, background: color.neutralBg, borderColor: color.neutralBorder, color: color.neutralText, "& svg": { ...notice["& svg"], color: color.neutralIcon } },
  suggestion: {
    ...plainButton,
    ...pill,
    gap: "6px",
    padding: "5px 10px",
    minHeight: 28,
    boxSizing: "border-box",
    border: `1px solid ${color.brandBorder}`,
    background: color.surface,
    color: color.brand,
    ...font.smallStrong,
    "& svg": { width: 12, height: 12 },
    "&:hover": { background: color.brandFaint },
  },
  versionLine: { display: "flex", alignItems: "center", gap: "8px", ...font.meta, color: color.faint },
  versionChip: {
    ...plainButton,
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    padding: "3px 8px",
    borderRadius: radius.versionChip,
    background: color.chipBg,
    color: color.body,
    ...font.meta,
    fontWeight: 700,
    "& svg": { width: 12, height: 12 },
    "&:hover": { background: color.lightBorder },
  },
  checklist: { display: "flex", flexDirection: "column", gap: "12px", py: "3px" },
  step: (state: "done" | "active" | "todo") => ({
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontSize: "13px",
    fontWeight: 600,
    color: state === "todo" ? color.faint : color.ink,
  }),
  stepMark: (state: "done" | "active" | "todo") => ({
    boxSizing: "border-box",
    width: 22,
    height: 22,
    borderRadius: "50%",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    background: state === "done" ? color.successBg : state === "active" ? color.brandTint : "transparent",
    border: state === "todo" ? `2px solid ${color.border}` : 0,
    color: color.successText,
    "& svg": { width: 13, height: 13 },
  }),
  typing: { display: "flex", alignItems: "center", gap: "10px", ...font.small, fontSize: "13px", color: color.sub },
  dots: (size: number) => ({
    display: "inline-flex",
    gap: "4px",
    "& span": {
      width: size,
      height: size,
      borderRadius: "50%",
      background: color.brand,
      animation: "aiwfPulse 1.2s ease-in-out infinite",
    },
    "& span:nth-of-type(1)": { opacity: 0.35 },
    "& span:nth-of-type(2)": { opacity: 0.6, animationDelay: "0.15s" },
    "& span:nth-of-type(3)": { opacity: 0.9, animationDelay: "0.3s" },
    "@keyframes aiwfPulse": { "0%, 100%": { transform: "scale(1)" }, "50%": { transform: "scale(0.7)" } },
  }),
  note: { ...font.meta, color: color.faint },

  // try chips + composer
  tryArea: { flexShrink: 0, pb: "12px" },
  tryHeading: { ...font.eyebrow, letterSpacing: "0.66px", color: color.faint, px: { xs: `${layout.phoneChatPadX}px`, md: `${layout.chatPadX}px` }, pb: "9px" },
  tryChips: (scroll: boolean) => ({
    display: "flex",
    gap: "8px",
    flexWrap: scroll ? "nowrap" : "wrap",
    overflowX: scroll ? "auto" : "visible",
    px: { xs: `${layout.phoneChatPadX}px`, md: `${layout.chatPadX}px` },
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
  }),
  tryChip: {
    ...plainButton,
    ...pill,
    boxSizing: "border-box",
    flexShrink: 0,
    minHeight: 29,
    padding: "6px 11px",
    border: `1px solid ${color.border}`,
    background: color.surface,
    color: color.body,
    ...font.smallStrong,
    "&:hover": { background: color.subtle },
    "&[disabled]": { opacity: 0.5, cursor: "default" },
  },
  composerWrap: {
    flexShrink: 0,
    px: { xs: `${layout.phoneChatPadX}px`, md: `${layout.chatPadX}px` },
    pb: { xs: "calc(16px + env(safe-area-inset-bottom))", md: "22px" },
    [SHORT]: { position: "sticky", bottom: 0, zIndex: 1, pt: "8px", pb: "calc(8px + env(safe-area-inset-bottom))", background: color.surface },
  },
  composer: (focused: boolean) => ({
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "12px 12px 10px 14px",
    borderRadius: radius.composer,
    border: `1px solid ${focused ? color.brand : color.border}`,
    boxShadow: focused ? `0 0 0 3px ${color.composerRing}` : "none",
    background: color.surface,
    transition: "border-color .15s ease, box-shadow .15s ease",
  }),
  textarea: {
    width: "100%",
    minHeight: 42,
    maxHeight: 160,
    resize: "none",
    border: 0,
    outline: 0,
    padding: 0,
    background: "transparent",
    fontFamily: "inherit",
    ...font.body,
    color: color.ink,
    "&::placeholder": { color: color.placeholder, opacity: 1 },
  },
  composerBar: { display: "flex", alignItems: "center", gap: "10px" },
  counter: { ml: "auto", ...font.meta, color: color.faint },
  sendButton: {
    ...plainButton,
    width: 36,
    height: 36,
    ...touch,
    borderRadius: radius.iconButton,
    background: color.brand,
    color: color.surface,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    "& svg": { width: 18, height: 18 },
    "&[disabled]": { background: color.chipBg, color: color.faint, cursor: "default" },
  },

  // ------------------------------------------------------------ preview
  preview: { flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", background: color.canvas, position: "relative", [SHORT]: { minHeight: 560 } },
  previewHeader: {
    boxSizing: "border-box",
    minHeight: layout.previewHeaderHeight,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: "14px",
    px: { xs: `${layout.phoneChatPadX}px`, md: `${layout.headerPadX}px` },
    py: { xs: "10px", md: 0 },
    background: color.surface,
    borderBottom: `1px solid ${color.divider}`,
  },
  previewHeading: { minWidth: 96, flexShrink: 1 },
  previewTitle: { ...font.control, color: color.ink },
  previewMeta: { ...font.small, color: color.sub, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  previewSpacer: { flex: 1, minWidth: 0 },
  headerRule: { width: "1px", height: 26, background: color.divider, flexShrink: 0 },
  thankYou: { display: "inline-flex", alignItems: "center", gap: "4px", ...font.smallStrong, color: color.body, whiteSpace: "nowrap", cursor: "pointer" },
  // Under the previewing banner the canvas keeps a shorter top gap: the banner already spaces it from the toolbar.
  canvas: (underBanner: boolean) => ({
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    boxSizing: "border-box",
    p: { xs: `${layout.phoneCanvasPad}px`, md: `${layout.canvasPad}px` },
    ...(underBanner ? { pt: "12px" } : {}),
  }),
  // On a phone the banner can take ~110 px, so the frame takes what is left rather than scrolling the canvas too.
  desktopFrame: { width: "100%", maxWidth: layout.desktopMaxWidth, height: "100%", minHeight: { xs: 320, md: 480 }, display: "flex" },
  iframe: { flex: 1, width: "100%", height: "100%", border: 0, display: "block", background: color.surface },
  phoneFrame: {
    boxSizing: "border-box",
    width: layout.phoneWidth + layout.phoneBezel * 2,
    height: `min(${layout.phoneMaxHeight + layout.phoneBezel * 2}px, 100%)`,
    minHeight: 520,
    flexShrink: 0,
    p: `${layout.phoneBezel}px`,
    borderRadius: `${layout.phoneRadius}px`,
    background: color.dark,
    boxShadow: `0 24px 48px ${color.panelShadow}`,
  },
  phoneScreen: { height: "100%", borderRadius: `${layout.phoneScreenRadius}px`, overflow: "hidden", display: "flex", flexDirection: "column", background: color.canvas },
  phoneStatus: { height: layout.phoneStatusHeight, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", px: "26px", ...font.smallBold, color: color.ink },
  phoneNotch: { width: 110, height: 24, borderRadius: "14px", background: color.dark },
  /** Over the page, below the preview header; it stops short of an open versions panel. */
  // Sits above the canvas, in the flow: when it wraps to more lines it moves the form down rather than covering it.
  previewingBanner: (besidePanel: boolean) => ({
    position: "relative",
    flexShrink: 0,
    mt: { xs: "12px", md: "16px" },
    ml: { xs: "12px", md: "36px" },
    mr: { xs: "12px", md: besidePanel ? `${layout.versionsWidth + 36}px` : "36px" },
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
    pl: "16px",
    pr: "12px",
    py: "10px",
    borderRadius: radius.card,
    background: color.dark,
    color: color.surface,
    boxShadow: `0 8px 12px ${color.bannerShadow}`,
    "& svg": { width: 16, height: 16 },
  }),
  // The label keeps a readable width; past it the buttons wrap onto their own line.
  bannerLabel: { flex: "1 1 220px", minWidth: 0, fontSize: "13px", fontWeight: 600 },
  bannerGhost: { ...plainButton, height: 32, px: "12px", borderRadius: radius.smallTile, background: color.darkOverlay, color: color.surface, ...font.smallBold },
  smallButton: { height: 32, px: "12px", "& svg": { width: 14, height: 14 } },

  // ------------------------------------------------------------ versions
  versionsOverlay: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: `min(${layout.versionsWidth}px, 100%)`,
    zIndex: 3,
    display: "flex",
    flexDirection: "column",
    background: color.surface,
    borderLeft: `1px solid ${color.lightBorder}`,
    boxShadow: `-12px 0 20px ${color.panelShadow}`,
    [SHORT]: { position: "fixed", top: 0 },
  },
  versionsSheet: { position: "absolute", inset: 0, zIndex: 3, display: "flex", flexDirection: "column", background: color.surface, [SHORT]: { position: "fixed", zIndex: 1200 } },
  versionsHeader: { display: "flex", alignItems: "center", gap: "12px", px: "22px", py: "20px", borderBottom: `1px solid ${color.hairline}`, "& > svg": { width: 20, height: 20, color: color.ink } },
  versionsList: { flex: 1, minHeight: 0, overflowY: "auto", pt: "12px", px: "14px", display: "flex", flexDirection: "column", gap: "6px" },
  versionRow: (selected: boolean) => ({
    boxSizing: "border-box",
    p: "14px",
    borderRadius: radius.card,
    border: `1px solid ${selected ? color.brandBorder : "transparent"}`,
    background: selected ? color.brandFaint : "transparent",
    "&:hover": { background: selected ? color.brandFaint : color.subtle },
  }),
  versionSelect: {
    ...plainButton,
    boxSizing: "border-box",
    width: "100%",
    textAlign: "left",
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    borderRadius: radius.smallTile,
  },
  versionTile: (selected: boolean) => ({
    width: 38,
    height: 38,
    flexShrink: 0,
    borderRadius: radius.iconButton,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "13px",
    fontWeight: 800,
    background: selected ? color.brand : color.chipBg,
    color: selected ? color.surface : color.body,
  }),
  versionText: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column" },
  versionTitle: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px", fontSize: "14px", fontWeight: 700, color: color.ink },
  versionWhen: { display: "block", ...font.small, color: color.sub, pt: "3px" },
  /** Under the row's text, past the 38px tile and its 12px gap. */
  versionActions: { display: "flex", flexWrap: "wrap", gap: "8px", pt: "12px", pl: "50px" },
  versionDivider: { height: "1px", background: color.hairline, mx: "14px", flexShrink: 0 },
  versionsFoot: { px: "22px", pt: "16px", pb: "22px", flexShrink: 0 },

  // ------------------------------------------------------------ publish dialog
  dialogBody: { display: "flex", flexDirection: "column", gap: "14px" },
  replaceNotice: { ...notice, background: color.infoBg, borderColor: color.infoBorder, color: color.infoText, "& svg": { ...notice["& svg"], color: color.infoText } },
  sectionHeading: { display: "flex", alignItems: "center", gap: "8px", ...font.eyebrow, letterSpacing: "0.66px", color: color.sub, "& svg": { width: 14, height: 14 } },
  sectionBody: { ...font.small, color: color.sub, mt: "-6px" },
  pageText: { boxSizing: "border-box", padding: "9px 12px", borderRadius: radius.smallTile, borderLeft: `3px solid ${color.brand}`, background: color.subtle, fontSize: "13px", fontWeight: 600, color: color.ink, overflowWrap: "anywhere" },
  fieldWarning: { ...notice, gap: "10px", padding: "10px 12px", background: color.warnBg, borderColor: color.warnBorder, color: color.warnText, "& svg": { ...notice["& svg"], color: color.warnText } },
  fieldWarningTitle: { fontSize: "13px", fontWeight: 700, color: color.warnTitle },

  // ------------------------------------------------------------ build-choice dialog
  choiceBody: { display: "flex", flexDirection: "column", gap: "14px" },
  choiceCard: (selected: boolean) => ({
    ...plainButton,
    boxSizing: "border-box",
    width: "100%",
    textAlign: "left",
    display: "flex",
    alignItems: "flex-start",
    gap: "14px",
    p: "16px",
    borderRadius: radius.card,
    border: `1px solid ${selected ? color.brand : color.border}`,
    background: selected ? color.brandFaint : color.surface,
    "&:hover": { borderColor: selected ? color.brand : color.strongBorder },
  }),
  radioMark: (selected: boolean) => ({
    display: "block",
    boxSizing: "border-box",
    mt: "2px",
    width: 18,
    height: 18,
    flexShrink: 0,
    borderRadius: "50%",
    border: `2px solid ${selected ? color.brand : color.strongBorder}`,
    position: "relative",
    "&::after": selected
      ? { content: '""', position: "absolute", top: 3, left: 3, width: 8, height: 8, borderRadius: "50%", background: color.brand }
      : {},
  }),
  choiceTile: (brand: boolean) => ({
    width: 40,
    height: 40,
    flexShrink: 0,
    borderRadius: radius.iconButton,
    background: brand ? color.brandTint : color.chipBg,
    color: brand ? color.brand : color.body,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& svg": { width: 18, height: 18 },
  }),
  choiceText: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column" },
  choiceTitle: { display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", fontWeight: 700, color: color.ink },
  newBadge: { ...pill, ...font.badge, padding: "4px 8px", background: color.brand, color: color.surface },
  choiceDescription: { display: "block", ...font.small, lineHeight: "18px", color: color.sub, pt: "4px" },
  describe: { display: "flex", flexDirection: "column", gap: "8px", px: "2px" },
  describeLabel: { ...font.smallBold, color: color.ink, "& span": { fontWeight: 500, color: color.sub } },
  describeInput: {
    boxSizing: "border-box",
    width: "100%",
    minHeight: 92,
    resize: "vertical",
    padding: "12px 14px",
    borderRadius: radius.notice,
    border: `1px solid ${color.border}`,
    background: color.fieldBg,
    fontFamily: "inherit",
    fontSize: "13px",
    fontWeight: 500,
    lineHeight: "20px",
    color: color.ink,
    outline: 0,
    "&::placeholder": { color: color.placeholder, opacity: 1 },
    "&:focus": { borderColor: color.brand },
  },
  describeCounter: { alignSelf: "flex-end", ...font.small, color: color.sub },
  describeChips: { display: "flex", flexWrap: "wrap", gap: "8px" },
  continueCard: { boxSizing: "border-box", display: "flex", flexDirection: "column", gap: "12px", p: "16px", borderRadius: radius.card, border: `1px solid ${color.brand}`, background: color.brandFaint },
  continueTop: { display: "flex", gap: "14px", alignItems: "flex-start" },
  thumb: {
    boxSizing: "border-box",
    width: 64,
    height: 80,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    gap: "5px",
    px: "7px",
    py: "8px",
    borderRadius: radius.smallTile,
    border: `1px solid ${color.lightBorder}`,
    background: color.canvas,
    "& span": { height: 5, borderRadius: "3px", background: color.border },
    "& span:nth-of-type(1)": { width: "52%", height: 6, background: color.ink, opacity: 0.5 },
    "& span:nth-of-type(3)": { height: 7, background: color.brandTint },
    "& span:last-of-type": { mt: "auto", width: "45%", height: 7, background: color.successText },
  },
  continueEyebrow: { display: "flex", alignItems: "center", gap: "6px", ...font.eyebrow, letterSpacing: "0.66px", color: color.brand, "& svg": { width: 12, height: 12 } },
  continueTitle: { ...font.panelTitle, color: color.ink, pt: "6px", overflowWrap: "anywhere" },
  continueMeta: { ...font.small, color: color.sub, pt: "4px" },
  continueActions: { display: "flex", alignItems: "center", gap: "14px" },
  removeLink: { ...plainButton, ml: "auto", ...font.smallBold, color: color.sub, textDecoration: "underline", minHeight: 32 },
  orDivider: { display: "flex", alignItems: "center", gap: "10px", ...font.eyebrow, letterSpacing: "0.66px", color: color.faint, "&::before, &::after": { content: '""', flex: 1, height: "1px", background: color.lightBorder } },

  // ------------------------------------------------------------ selection prompt
  selectionPrompt: (pos: { top?: number; left?: number; isMobile: boolean }) => ({
    position: "fixed",
    ...(pos.isMobile
      ? {
          bottom: tokens.layout.selectionPromptMobileBottom,
          left: 16,
          right: 16,
          height: 40,
        }
      : {
          top: pos.top ?? 0,
          left: pos.left ?? 0,
          width: 340,
          height: 40,
        }),
    zIndex: tokens.layout.selectionPromptZIndex,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    px: "12px",
    background: color.surface,
    border: `1px solid ${color.lightBorder}`,
    borderRadius: tokens.layout.selectionPromptBorderRadius,
    boxShadow: "0 8px 24px rgba(33, 21, 71, 0.14), 0 2px 6px rgba(0, 0, 0, 0.05)",
    boxSizing: "border-box",
    pointerEvents: "auto",
    transition: "top 0.1s ease-out, left 0.1s ease-out, border-color 0.15s ease, box-shadow 0.15s ease",
    "&:focus-within": {
      borderColor: color.brand,
      boxShadow: `0 0 0 3px ${color.composerRing}, 0 8px 24px rgba(33, 21, 71, 0.14)`,
    },
  }),
  selectionPromptIcon: {
    width: 17,
    height: 17,
    color: color.brand,
    flexShrink: 0,
  },
  selectionPromptInput: {
    flex: 1,
    minWidth: 0,
    height: "100%",
    background: "transparent",
    border: "none",
    outline: "none",
    color: color.ink,
    fontSize: "13.5px",
    fontWeight: 400,
    fontFamily: "inherit",
    lineHeight: "normal",
    p: 0,
    m: 0,
    "&::placeholder": {
      color: color.placeholder,
      opacity: 1,
    },
  },
  selectionPromptSend: (canSend: boolean) => ({
    width: 28,
    height: 28,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "6px",
    border: "none",
    cursor: canSend ? "pointer" : "default",
    background: canSend ? color.brand : color.chipBg,
    color: canSend ? color.surface : color.faint,
    transition: "all 0.15s ease",
    p: 0,
    "&:hover": canSend
      ? {
          background: color.dark,
          color: color.surface,
        }
      : {},
    "& svg": {
      width: 15,
      height: 15,
    },
  }),

  // ------------------------------------------------------------ whole-page states
  stateBox: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: "24px" },
  stateCard: { maxWidth: 440, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: "14px", "& p": { m: 0, ...font.body, color: color.body } },
};

const aiWebFormStyles = raw as unknown as {
  [K in keyof typeof raw]: (typeof raw)[K] extends (...args: infer A) => any
    ? (...args: A) => SxProps<Theme>
    : SxProps<Theme>;
};

export default aiWebFormStyles;
